const DEFAULT_TEAM_NAME = 'Без команды'
const HIDDEN_TEAM_MARKERS = new Set(['forbidden', 'доступ запрещен', 'доступ запрещён'])

export function normalizeTeamName(value) {
  const team = String(value || '').trim()
  if (!team || HIDDEN_TEAM_MARKERS.has(team.toLowerCase())) {
    return DEFAULT_TEAM_NAME
  }
  return team
}

function toNumber(value) {
  const number = Number(value)
  return Number.isFinite(number) ? number : 0
}

function parseDate(value) {
  if (!value) return null
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value
  const date = new Date(value)
  if (!Number.isNaN(date.getTime())) return date

  const match = String(value).match(/^(\d{2})\.(\d{2})\.(\d{4})$/)
  if (match) {
    return new Date(Number(match[3]), Number(match[2]) - 1, Number(match[1]))
  }
  return null
}

function quarterOf(date) {
  return Math.floor(date.getMonth() / 3) + 1
}

function rowTeam(row) {
  return normalizeTeamName(row?.team ?? row?.команда ?? row?.team_name)
}

function stageId(row) {
  return row?.id_стадии ?? row?.stage_id ?? row?.id ?? row?.stage_name ?? ''
}

function stageBudget(row) {
  return toNumber(
    row?.доход_сотрудника_по_стоимости
      ?? row?.employee_income_cost
      ?? row?.income
      ?? row?.budget
      ?? row?.my_share,
  )
}

function stageActed(row) {
  return toNumber(row?.доход_по_ранее_заактированной ?? row?.previously_act_amount ?? row?.employee_income_prev_act)
    + toNumber(row?.заактировано_в_2025 ?? row?.acts_income_amount ?? row?.employee_income_act)
}

function actAmount(row) {
  return toNumber(row?.employee_income_act ?? row?.income ?? row?.amount ?? row?.сумма)
}

function matchesPeriod(row, { periodType, year, quarter }) {
  const date = parseDate(row?.act_date ?? row?.date ?? row?.дата)
  if (!date || date.getFullYear() !== Number(year)) return false
  if (periodType === 'quarter') return quarterOf(date) === Number(quarter)
  return true
}

export function filterRowsByTeam(rows = [], team = 'all') {
  if (!team || team === 'all') return rows
  const selectedTeam = normalizeTeamName(team)
  return rows.filter((row) => rowTeam(row) === selectedTeam)
}

export function buildTeamOptions(employee = {}) {
  const teams = new Set()

  ;(employee.team_assignments || []).forEach((assignment) => {
    teams.add(normalizeTeamName(assignment.team_name ?? assignment.team))
  })
  ;(employee.b2_stages || []).forEach((stage) => {
    teams.add(rowTeam(stage))
  })
  ;(employee.b2_acts || []).forEach((act) => {
    teams.add(rowTeam(act))
  })

  teams.delete(DEFAULT_TEAM_NAME)

  return [
    { value: 'all', label: 'Все' },
    ...Array.from(teams).sort((a, b) => a.localeCompare(b, 'ru')).map((team) => ({ value: team, label: team })),
  ]
}

export function getRemainingStatusBucket(status) {
  const normalized = String(status || '').trim().toLowerCase()
  if (['работы завершены', 'закрыта', 'закрыт', 'завершена', 'завершен'].includes(normalized)) {
    return 'doneWork'
  }
  if (normalized === 'не в работе' || normalized === 'новая') {
    return 'new'
  }
  return 'active'
}

export function buildEmployeeIncomeModel(employee = {}, options = {}) {
  const periodType = options.periodType === 'quarter' ? 'quarter' : 'year'
  const year = Number(options.year || new Date().getFullYear())
  const quarter = Number(options.quarter || quarterOf(new Date()))
  const team = options.team || 'all'

  const allActs = filterRowsByTeam(employee.b2_acts || [], team)
  const periodActs = allActs.filter((act) => matchesPeriod(act, { periodType, year, quarter }))

  const breakdownByStage = periodActs.reduce((acc, act) => {
    const stage = stageId(act) || 'Без стадии'
    const project = act.contract || act.contract_number || ''
    const key = `${project}::${stage}::${rowTeam(act)}`
    if (!acc.has(key)) {
      acc.set(key, {
        project,
        stage,
        stageId: stage,
        team: rowTeam(act),
        actsCount: 0,
        amount: 0,
      })
    }
    const item = acc.get(key)
    item.actsCount += 1
    item.amount += actAmount(act)
    return acc
  }, new Map())

  const remainingStages = filterRowsByTeam(employee.b2_stages || [], team)
    .map((stage) => {
      const id = stageId(stage)
      const budget = stageBudget(stage)
      const acted = stageActed(stage)
      return {
        stageId: id,
        stage,
        object: stage.объект || stage.object_name || '',
        team: rowTeam(stage),
        budget,
        acted,
        remaining: Math.max(budget - acted, 0),
        status: stage.статус_стадии || stage.status || '',
        statusBucket: getRemainingStatusBucket(stage.статус_стадии || stage.status),
        dueDate: stage.срок_исполнения || stage.planned_date || stage.due_date || '',
      }
    })

  const quarters = [1, 2, 3, 4].map((itemQuarter) => ({
    quarter: itemQuarter,
    amount: allActs
      .filter((act) => matchesPeriod(act, { periodType: 'quarter', year, quarter: itemQuarter }))
      .reduce((sum, act) => sum + actAmount(act), 0),
  }))

  return {
    heroAmount: periodActs.reduce((sum, act) => sum + actAmount(act), 0),
    breakdown: Array.from(breakdownByStage.values()),
    remainingStages,
    quarters,
  }
}
